import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelDynamic as _vModelDynamic, mergeProps as _mergeProps, createElementVNode as _createElementVNode, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "base-form-input__append"
}
const _hoisted_2 = ["placeholder", "type", "title"]
const _hoisted_3 = {
  key: 1,
  class: "base-form-input__prepend"
}

import { computed, ref, watch, ObjectDirective } from 'vue';
import { MaskTokens } from 'maska/dist/types/tokens';
import { Mask } from 'maska';
// instructions for maska lib you can find here
// https://beholdr.github.io/maska/#/?id=live-demo


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseFormInput',
  props: {
    placeholder: {},
    modelValue: { default: '' },
    type: { default: 'text' },
    isError: { type: Boolean },
    returnMaskedValue: { type: Boolean },
    mask: {},
    maskTokens: { default: () => ({}) },
    disallowSpecialChars: { type: Boolean },
    disallowNumbers: { type: Boolean },
    allowIntegerOnly: { type: Boolean },
    allowChars: {},
    disallowChars: {},
    append: { type: Boolean },
    prepend: { type: Boolean },
    readonly: { type: Boolean },
    disabled: { type: Boolean },
    autoFocused: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

// TODO: refactor to move mask logic to separated component
const props = __props;

const emit = __emit;

const localValue = ref('');
const focused = ref(false);

const mask = new Mask({
  mask: props.mask,
  tokens: props.maskTokens,
});
const unmaskedLocalValue = computed(() => mask.unmasked(localValue.value));
const returnValue = computed(() => (
  (!props.mask || props.returnMaskedValue) ? localValue.value : unmaskedLocalValue.value
));

function getEscapedRegExp(str: string) {
  // If you need to use any of the special characters literally (actually searching for a "*", for instance),
  // you must escape it by putting a backslash in front of it.
  // eslint-disable-next-line no-useless-escape
  return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

const specialCharacters = computed(() => {
  // depending on props we are adding or removing characters
  let charsString = '';
  if (props.disallowSpecialChars) charsString += '@&#+$~%^:*?<>{}[]!)(_/\\|=`;"';
  if (props.disallowNumbers) charsString += '0123456789';
  if (props.disallowChars) charsString += props.disallowChars;
  if (props.allowChars) {
    [...charsString].forEach((char) => {
      charsString = charsString.replace(char, '');
    });
  }
  return getEscapedRegExp(charsString);
});
// create regex from characters what to exclude
const disallowCharsRegex = computed(() => new RegExp(`[${specialCharacters.value}]`, 'g'));

function onFilterSpecialCharsKeyDown(event: KeyboardEvent) {
  // function to exclude characters on typing
  if (specialCharacters.value.includes(event.key)) {
    event.preventDefault();
  }
}

function onFilterSpecialCharsBeforeInput(event: InputEvent) {
  if (disallowCharsRegex.value.test(event.data || '')) {
    event.preventDefault();
  }
}

function onIntegerKeyDown(event: KeyboardEvent) {
  if (['.', ',', '+', '-', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
}

function onIntegerBeforeInput(event: InputEvent) {
  if (event.data === '.') event.preventDefault();
}

function onBeforeInput(event: InputEvent) {
  if (props.allowIntegerOnly) onIntegerBeforeInput(event);
  onFilterSpecialCharsBeforeInput(event);
}

function onKeyDown(event: KeyboardEvent) {
  if (props.allowIntegerOnly) onIntegerKeyDown(event);
  onFilterSpecialCharsKeyDown(event);
}

function onInput(value: string) {
  // characters to check to replace
  const charsToCheck = props.allowIntegerOnly ? /^0+|\D+/g : disallowCharsRegex.value;
  // set filtered value
  localValue.value = value.replace(charsToCheck, '');
  // if maska then set masked value
  if (props.mask) localValue.value = mask.masked(localValue.value);
  emit('update:modelValue', returnValue.value);
}

function onInputEvent() {
  onInput(localValue.value);
}

// On user paste from buffer
function onPaste(event: ClipboardEvent) {
  const clipboardData = event?.clipboardData?.getData('text/plain');
  const clipboardDataTrimmed = String(clipboardData)
    .replace(/\t/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
  // wait until variable changed
  setTimeout(() => onInput(clipboardDataTrimmed));
}

function onFocus() {
  focused.value = true;
}

function onBlur() {
  focused.value = false;
}
const vFocus: ObjectDirective<HTMLElement, boolean | undefined> = {
  mounted: (el, { value }) => value && el.focus(),
};
// check modelValue data
if (props.modelValue !== '') onInput(props.modelValue);
watch(() => props.modelValue, () => onInput(props.modelValue));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["BaseFormInput base-form-input", {'is--error': _ctx.isError, 'is--focused': focused.value, 'is--readonly': _ctx.readonly, 'is--disabled': _ctx.disabled}])
  }, [
    (_ctx.append)
      ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
          _renderSlot(_ctx.$slots, "append")
        ]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("input", _mergeProps({
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localValue).value = $event)),
      class: "base-form-input__input",
      placeholder: _ctx.placeholder,
      type: _ctx.type,
      title: localValue.value,
      tabindex: "0"
    }, _ctx.$attrs, {
      onInput: onInputEvent,
      onPaste: onPaste,
      onKeydown: onKeyDown,
      onBeforeinput: onBeforeInput,
      onFocus: onFocus,
      onBlur: onBlur
    }), null, 16, _hoisted_2), [
      [_vModelDynamic, localValue.value],
      [vFocus, _ctx.autoFocused]
    ]),
    (_ctx.prepend)
      ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
          _renderSlot(_ctx.$slots, "prepend")
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})